import { z } from "zod";

// =========================================================================
// VALIDATION RESULT TYPES
// =========================================================================

export type ValidationSuccess<T> = {
  success: true;
  data: T;
};

export type ValidationError = {
  success: false;
  error: {
    issues: Array<{
      path: string[];
      message: string;
      code: string;
    }>;
    message: string;
  };
};

export type ValidationResult<T> = ValidationSuccess<T> | ValidationError;

// =========================================================================
// SAFE VALIDATION WRAPPER FOR WORKERS
// =========================================================================

/**
 * Valida datos con un schema de Zod y devuelve un resultado tipo-seguro
 *
 * @example
 * ```typescript
 * const result = safeValidate(CreateOrderRequestSchema, requestBody);
 *
 * if (!result.success) {
 *   return reply.code(400).send({ error: result.error });
 * }
 *
 * // TypeScript sabe que result.data es CreateOrderRequest
 * const order = result.data;
 * ```
 */
export function safeValidate<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): ValidationResult<z.infer<T>> {
  const result = schema.safeParse(data);

  if (result.success) {
    return {
      success: true,
      data: result.data,
    };
  }

  return {
    success: false,
    error: {
      issues: result.error.issues.map((issue) => ({
        path: issue.path.map(String),
        message: issue.message,
        code: issue.code,
      })),
      message: "Validation failed",
    },
  };
}

// =========================================================================
// PARTIAL VALIDATION (para PATCH/PUT)
// =========================================================================

/**
 * Convierte un schema en uno donde todos los campos son opcionales
 * Útil para endpoints PATCH que aceptan actualizaciones parciales
 *
 * @example
 * ```typescript
 * const PartialUpdateSchema = makePartial(UpdateFormRequestSchema);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function makePartial<T extends z.ZodObject<any>>(schema: T) {
  return schema.partial();
}

// =========================================================================
// DEFAULTS (para derivar un schema "con defaults" desde un schema base)
// =========================================================================

/**
 * Aplica `.default()` a un subconjunto de campos de un ZodObject, reutilizando los
 * tipos del shape base. Evita repetir cada campo dos veces al derivar un schema "con
 * defaults" de un schema "base" (sin defaults).
 *
 * @example
 * const FooSchema = withDefaults(FooBaseSchema, { enabled: true, retries: 3 });
 */
export function withDefaults<
  Shape extends z.ZodRawShape,
  D extends { [K in keyof Shape]?: z.input<Shape[K]> },
>(schema: z.ZodObject<Shape>, defaults: D) {
  const shape = schema.shape as unknown as Record<string, z.ZodTypeAny>;
  const extension = {} as {
    [K in keyof D & keyof Shape]: z.ZodDefault<Shape[K]>;
  };

  for (const key of Object.keys(defaults) as Array<keyof D & keyof Shape>) {
    extension[key] = shape[key as string].default(
      defaults[key] as never
    ) as unknown as z.ZodDefault<Shape[typeof key]>;
  }

  return schema.extend(extension);
}

// =========================================================================
// CUSTOM VALIDATORS
// =========================================================================

/**
 * Valida que una fecha sea futura
 */
export const futureDateValidator = z
  .string()
  .datetime()
  .refine((date) => new Date(date) > new Date(), {
    message: "La fecha debe ser futura",
  });

/**
 * Valida que un precio sea mayor a un mínimo
 */
export function minPriceValidator(minAmount: number) {
  return z.number().min(minAmount, `El precio debe ser al menos ${minAmount}`);
}

/**
 * Valida un email y lo normaliza a lowercase
 */
export const normalizedEmailSchema = z
  .string()
  .email("Email inválido")
  .transform((email) => email.toLowerCase().trim());

/**
 * Valida un slug (URL-friendly string)
 */
export const slugSchema = z
  .string()
  .min(3)
  .max(200)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Slug debe contener solo letras minúsculas, números y guiones"
  );

// =========================================================================
// CONDITIONAL VALIDATION
// =========================================================================

/**
 * Schema que requiere field B solo si field A tiene un valor específico
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   payment_method: z.enum(['card', 'cash']),
 *   card_number: z.string().optional(),
 * }).refine(
 *   requireFieldIf('payment_method', 'card', 'card_number'),
 *   { message: 'Se requiere número de tarjeta para pago con tarjeta' }
 * );
 * ```
 */
export function requireFieldIf<T extends Record<string, unknown>>(
  triggerField: keyof T,
  triggerValue: unknown,
  requiredField: keyof T
) {
  return (data: T) => {
    if (data[triggerField] === triggerValue) {
      return data[requiredField] !== undefined && data[requiredField] !== null;
    }
    return true;
  };
}

// =========================================================================
// SHOP / LOGISTICS
// =========================================================================

/**
 * In-store pickup / pickup points are only supported when the shop country is Colombia.
 * Expects ISO 3166-1 alpha-2 (e.g. "CO"); comparison is case-insensitive.
 */
export function isPickupOfficesSupportedForShopCountry(
  countryCode: string | null | undefined
): boolean {
  return (countryCode ?? "").trim().toUpperCase() === "CO";
}
